import * as React from 'react';

import Mousetrap from 'mousetrap';

import type {
    LogActivityInput,
    GetPotentialMeetings_potentialMeetings as Meeting,
    GetActivity_activity as Activity,
} from 'nutshell-graphql-types';

import {appHistory} from '../../../../history';
import {addNotification} from '../../../../notifications/index';
import {
    readLocalStorage,
    removeLocalStorageItem,
    writeLocalStorage,
} from '../../../../util/localStorage';

import {Header, Footer, NotesSection, MeetingSection} from '.';

import styles from './activity-log-modal.module.css';

const STORAGE_KEY_PREFIX = 'activity_note_';

type Props = {
    onLogActivity: (input: LogActivityInput) => Promise<any>;
    isLogging: boolean;
    isFeatureGateModalOpen: boolean;
    setIsFeatureGateModalOpen: (isOpen: boolean) => void;
    onClose: () => void;
    activity: Activity;
};

export function ActivityLogModalContent(props: Props) {
    const {onLogActivity, onClose, activity} = props;

    const storageKey = `${STORAGE_KEY_PREFIX}${activity.id}`;
    const noteData = readLocalStorage(storageKey);
    const {text: initialText, html: initialHtml} = noteData
        ? JSON.parse(noteData)
        : {text: '', html: ''};

    const [noteText, setNoteText] = React.useState<string>(initialText);
    const [noteHtml, setNoteHtml] = React.useState<string>(initialHtml);
    const [shouldSummarize, setShouldSummarize] = React.useState<boolean>(false);
    const [shouldTranscribe, setShouldTranscribe] = React.useState<boolean>(true);
    const [selectedMeeting, setSelectedMeeting] = React.useState<Meeting | null | undefined>(
        activity.externalMeeting
    );
    const [activityTypeId, setActivityTypeId] = React.useState<string>(activity.activityType.id);

    const handleClose = React.useCallback(() => {
        removeLocalStorageItem(storageKey);
        onClose();
    }, [storageKey, onClose]);

    const handleLogActivity = React.useCallback(() => {
        onLogActivity({
            activity: {
                activityId: activity.id,
                logNote: noteText,
                shouldSummarize: selectedMeeting ? shouldSummarize : undefined,
                externalMeetingId: selectedMeeting ? selectedMeeting.id : undefined,
                shouldTranscribeZoomMeeting: shouldTranscribe,
                activityTypeId: activityTypeId,
            },
        }).then(() => {
            handleClose();

            addNotification('Success!', {
                body: 'Activity logged successfully',
                icon: 'check',
                onClick: () => {
                    appHistory.replace({
                        ...appHistory.location,
                        hash: `/${activity.id}`,
                        state: {modal: true},
                    });
                },
            });
        });
    }, [
        onLogActivity,
        activity.id,
        noteText,
        selectedMeeting,
        shouldSummarize,
        shouldTranscribe,
        activityTypeId,
        handleClose,
    ]);

    React.useEffect(() => {
        Mousetrap.bind(['ctrl+enter', 'meta+enter'], handleLogActivity);

        return () => {
            Mousetrap.unbind(['ctrl+enter', 'meta+enter']);
        };
    }, [handleLogActivity]);

    // Save both HTML and text to localStorage
    const handleSetNote = (textValue: string, htmlValue: string) => {
        setNoteText(textValue);
        setNoteHtml(htmlValue);

        const storageData = JSON.stringify({
            text: textValue,
            html: htmlValue,
        });

        writeLocalStorage(storageKey, storageData);
    };

    return (
        <>
            <div className={styles['content-container']}>
                <Header
                    activity={activity}
                    activityTypeId={activityTypeId}
                    setActivityTypeId={setActivityTypeId}
                    onClose={handleClose}
                />
                <NotesSection
                    setNote={handleSetNote}
                    onClose={handleClose}
                    initialValue={noteHtml}
                    initialTextValue={noteText}
                    onLogActivity={handleLogActivity}
                />
                {activity.externalMeetingServiceType && (
                    <MeetingSection
                        activityId={activity.id}
                        selectedMeeting={selectedMeeting}
                        setSelectedMeeting={setSelectedMeeting}
                        shouldSummarize={shouldSummarize}
                        setShouldSummarize={setShouldSummarize}
                        shouldTranscribe={shouldTranscribe}
                        setShouldTranscribe={setShouldTranscribe}
                        isFeatureGateModalOpen={props.isFeatureGateModalOpen}
                        setIsFeatureGateModalOpen={props.setIsFeatureGateModalOpen}
                        serviceType={activity.externalMeetingServiceType}
                    />
                )}
            </div>
            <Footer
                onLogActivity={handleLogActivity}
                isLogging={props.isLogging}
                onClose={handleClose}
            />
        </>
    );
}
